import type { WorkflowDocument } from '@/lib/services/correspondences/workflows/workflow-types';
import type { MatterFile } from '@/lib/services/files/types';

type RawAttachment = Record<string, unknown>;

/**
 * Convert matter files to WorkflowDocument format for compatibility
 */
export function convertDocumentsToWorkflowFormat(
  matterFiles: MatterFile[]
): WorkflowDocument[] {
  if (!matterFiles || matterFiles.length === 0) return [];

  return matterFiles.map((file) => ({
    id: file.id,
    originalFilename:
      file.originalFilename || file.displayName || `File ${file.id}`,
    createdAt: file.createdAt,
    updatedAt: file.updatedAt || file.createdAt,
    status: file.status || 'available',
    s3Key: file.s3Key || `/api/files/${file.id}/download`,
    imageS3Key: file.imageS3Key || null,
    imageMimeType: file.imageMimeType || null,
    mimeType: file.mimeType || 'application/pdf',
    fileType:
      typeof file.fileType === 'string'
        ? file.fileType
        : file.fileType?.slug || 'other',
    extractedData: file.extractedData || null,
    isCertifiedCopy: file.isCertifiedCopy || false,
    metadata: file.metadata || null,
    analysisMetadata: file.analysisMetadata || null,
    fileSizeKb: file.fileSizeKb || null,
    deletedAt: file.deletedAt || null
  })) as WorkflowDocument[];
}

/**
 * Convert draft attachment to WorkflowDocument format
 */
export function convertAttachmentToWorkflowDocument(
  att: RawAttachment | null | undefined
): WorkflowDocument | null {
  if (!att || typeof att !== 'object') return null;

  const get = <T>(key: string): T | undefined => att[key] as T | undefined;

  return {
    id: get<number>('id') ?? Math.random(),
    originalFilename:
      get<string>('originalFilename') ??
      get<string>('filename') ??
      'Attachment',
    createdAt: get<string>('createdAt') ?? new Date().toISOString(),
    updatedAt:
      get<string>('updatedAt') ??
      get<string>('createdAt') ??
      new Date().toISOString(),
    status: 'available' as const,
    s3Key: get<string>('s3Key') ?? get<string>('url') ?? '',
    imageS3Key:
      get<string>('imageS3Key') ?? get<string>('thumbnailUrl') ?? null,
    imageMimeType: get<string>('imageMimeType') ?? null,
    mimeType: get<string>('mimeType') ?? 'application/octet-stream',
    fileType: get<string>('fileType') ?? 'other',
    extractedData: get<Record<string, unknown>>('extractedData') ?? null,
    isCertifiedCopy: get<boolean>('isCertifiedCopy') ?? false,
    metadata: get<Record<string, unknown>>('metadata') ?? null,
    analysisMetadata: get<Record<string, unknown>>('analysisMetadata') ?? null,
    fileSizeKb: get<number>('fileSizeKb') ?? get<number>('size') ?? null,
    deletedAt: get<string>('deletedAt') ?? null
  } as WorkflowDocument;
}
